import { useCallback, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Drawer } from '../../../components/ui/Drawer';
import Notification, { NotificationContainer } from '../../../components/Notification';
import contractsService from '../../../services/compliance/contracts.service';
import {
  ContractImportPreview,
  ContractImportPreviewRow,
  ContractImportResult,
} from '../../../types/compliance';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_EXT = ['.xlsx', '.csv'];

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
  pending_renewal: 'Pending Renewal',
};

export interface ContractImportDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Fires once the batch has been created so the parent can refresh its list. */
  onImported: (result: ContractImportResult) => void;
}

/**
 * Three-step spreadsheet import: download template → upload (auto-validates
 * and shows a row-by-row preview) → Create. The same File is sent to both
 * the validate and import endpoints; the server re-validates on import, so
 * a 422 there replaces the preview with the server's view.
 */
export default function ContractImportDrawer({
  open,
  onClose,
  onImported,
}: ContractImportDrawerProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ContractImportPreview | null>(null);
  const [result, setResult] = useState<ContractImportResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const showServerError = useCallback((title: string, error: any) => {
    const detail = error?.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : error?.message || 'Unknown error';
    setErrorNotification({ title, message: msg });
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    setDownloading(true);
    try {
      await contractsService.downloadImportTemplate();
    } catch (error: any) {
      showServerError('Failed to download template', error);
    } finally {
      setDownloading(false);
    }
  }, [showServerError]);

  const validateFile = useCallback(
    async (f: File) => {
      setValidating(true);
      setPreview(null);
      setResult(null);
      try {
        setPreview(await contractsService.validateImport(f));
      } catch (error: any) {
        showServerError('Could not read the spreadsheet', error);
      } finally {
        setValidating(false);
      }
    },
    [showServerError],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] || null;
      reset();
      if (!f) return;
      if (f.size > MAX_BYTES) {
        setErrorNotification({
          title: 'File too large',
          message: `Max file size is 5 MB. You selected ${(f.size / 1024 / 1024).toFixed(2)} MB.`,
        });
        e.target.value = '';
        return;
      }
      const lower = f.name.toLowerCase();
      if (!ACCEPTED_EXT.some((ext) => lower.endsWith(ext))) {
        setErrorNotification({
          title: 'Unsupported file type',
          message: 'Upload the .xlsx template (or a .csv with the same headers).',
        });
        e.target.value = '';
        return;
      }
      setFile(f);
      void validateFile(f);
    },
    [reset, validateFile],
  );

  const handleImport = useCallback(async () => {
    if (!file || !preview || preview.error_count > 0) return;
    setImporting(true);
    try {
      const r = await contractsService.importContracts(file);
      setResult(r);
      onImported(r);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.preview) {
        // The server re-validated and found problems (e.g. someone created one
        // of these contracts in the meantime). Its preview is the truth.
        setPreview(detail.preview as ContractImportPreview);
        setErrorNotification({
          title: 'Nothing was created',
          message: 'The file no longer validates. Review the rows marked in red.',
        });
      } else {
        showServerError('Import failed — nothing was created', error);
      }
    } finally {
      setImporting(false);
    }
  }, [file, preview, onImported, showServerError]);

  const canCreate =
    !!file && !!preview && preview.error_count === 0 && preview.ok_count > 0 && !result;

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={handleClose}
        disabled={importing}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
      >
        {result ? 'Done' : 'Cancel'}
      </button>
      <button
        type="button"
        onClick={handleImport}
        disabled={!canCreate || importing || validating}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {importing ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <CloudArrowUpIcon className="w-4 h-4" />
            {preview
              ? `Create ${preview.ok_count} contract${preview.ok_count === 1 ? '' : 's'}`
              : 'Create contracts'}
          </>
        )}
      </button>
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={open}
        onClose={handleClose}
        title="Import Contracts from Spreadsheet"
        position="left"
        width="60vw"
        footer={footer}
        disableClickOutside
      >
        <div className="space-y-5">
          <section>
            <h3 className="text-sm font-semibold mb-1">Step 1 — Download the template</h3>
            <p className="text-xs text-muted mb-2">
              One contract per row. Dropdowns list the vendors, categories and departments that
              exist today, so add any missing vendors under Third Parties first.
            </p>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {downloading ? 'Downloading...' : 'Download Excel Template'}
            </button>
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-1">Step 2 — Upload the completed file</h3>
            <p className="text-xs text-muted mb-2">
              Accepts <code>.xlsx</code> or <code>.csv</code>, max 5 MB and 500 rows. Nothing is
              created until you review the preview and click Create.
            </p>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={handleFileChange}
              disabled={validating || importing}
              className="block w-full text-sm"
            />
            {file && (
              <p className="text-xs text-gray-500 mt-1">
                Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                {validating && ' · checking...'}
              </p>
            )}
          </section>

          {preview && !result && (
            <section>
              <h3 className="text-sm font-semibold mb-1">Step 3 — Review and create</h3>
              <PreviewSummary preview={preview} />
              {preview.file_warnings.length > 0 && (
                <ul className="mt-2 text-xs text-amber-700 dark:text-amber-300 list-disc pl-4">
                  {preview.file_warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
              <PreviewTable rows={preview.rows} />
            </section>
          )}

          {result && (
            <div className="p-3 border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950 rounded-lg">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 inline-flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5" />
                Created {result.created} contract{result.created === 1 ? '' : 's'}
              </h4>
              <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                They are in the Contracts list now. Open each one and use its Documents tab to
                attach the signed files.
              </p>
            </div>
          )}
        </div>
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={6000}
          />
        </NotificationContainer>
      )}
    </>
  );
}

function PreviewSummary({ preview }: { preview: ContractImportPreview }) {
  const hasErrors = preview.error_count > 0;
  return (
    <p
      className={`text-xs font-medium ${
        hasErrors ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'
      }`}
    >
      {preview.total} row{preview.total === 1 ? '' : 's'} · {preview.ok_count} ready ·{' '}
      {preview.error_count} with errors
      {hasErrors && ' — fix the spreadsheet and upload it again'}
    </p>
  );
}

const PREVIEW_HEADERS = [
  'Row',
  'Title',
  'Third Party',
  'Owner',
  'Start',
  'End',
  'Category',
  'Department',
  'Status',
  'Result',
];

function PreviewTable({ rows }: { rows: ContractImportPreviewRow[] }) {
  return (
    <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto max-h-[50vh] overflow-y-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
          <tr>
            {PREVIEW_HEADERS.map((h) => (
              <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.row}
              className={
                r.ok
                  ? 'border-t border-gray-100 dark:border-gray-800'
                  : 'border-t border-red-100 dark:border-red-900 bg-red-50 dark:bg-red-950/40'
              }
            >
              <td className="px-2 py-1 text-gray-500">{r.row}</td>
              <td className="px-2 py-1 max-w-[16rem] truncate" title={r.title}>
                {r.title || '—'}
              </td>
              <td className="px-2 py-1 whitespace-nowrap">{r.third_party_name || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{r.owner_display || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{r.start_date || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{r.end_date || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{r.category || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{r.department || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{STATUS_LABELS[r.status] ?? r.status}</td>
              <td className="px-2 py-1 min-w-[14rem]">
                {r.ok ? (
                  <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300">
                    <CheckCircleIcon className="w-4 h-4" /> Ready
                  </span>
                ) : (
                  <ul className="space-y-0.5 text-red-700 dark:text-red-300">
                    {r.errors.map((e, i) => (
                      <li key={`${e.field}-${i}`} className="flex items-start gap-1">
                        <ExclamationCircleIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          <strong>{e.field}</strong> {e.message}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
